<template>
  <div>
    <div
      v-show="modalActive"
      class="modal z-50 fixed w-full h-full top-0 left-0 flex items-center justify-center"
    >
      <div class="absolute w-full h-full bg-gray-900 opacity-50 modal-overlay"></div>

      <div
        class="container-modal z-50 mx-auto overflow-y-auto bg-white rounded shadow-lg modal-container"
      >
        <div
          @click="close"
          class="absolute md:inline flex top-0 right-0 z-50 flex-col items-center mt-4 mr-4 text-sm text-white cursor-pointer modal-close"
        >
          <svg
            class="text-white fill-current"
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
          >
            <path
              d="M14.53 4.53l-1.06-1.06L9 7.94 4.53 3.47 3.47 4.53 7.94 9l-4.47 4.47 1.06 1.06L9 10.06l4.47 4.47 1.06-1.06L10.06 9z"
            />
          </svg>
          <span class="text-sm">(Esc)</span>
        </div>

        <div class="px-6 py-4 text-left modal-content" :key="keyRender">
          <div class="flex items-center justify-between pb-3">
            <p class="titleBold">{{ title }}</p>
            <div class="z-50 cursor-pointer modal-close" @click="close">
              <svg
                class="text-black fill-current"
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 18 18"
              >
                <path
                  d="M14.53 4.53l-1.06-1.06L9 7.94 4.53 3.47 3.47 4.53 7.94 9l-4.47 4.47 1.06 1.06L9 10.06l4.47 4.47 1.06-1.06L10.06 9z"
                />
              </svg>
            </div>
          </div>

          <div class="grid grid-cols-1 gap-4 my-4">
            <Input
              :isError="error.name"
              @change="validateName"
              errorMessage="Nome da política inválido"
              width="100%"
              label="Nome da política"
              v-model="form.name"
              required
              data-testid="policy-name-input"
            />

            <v-autocomplete
              v-model="form.companyId"
              color="primary20"
              label="Empresa"
              :items="companiesFormatted"
              variant="outlined"
              data-testid="policy-company-select"
            />
          </div>

          <div class="flex justify-end">
            <OutlineButton
              @click="close"
              class="mr-4"
              text="Cancelar"
              width="120px"
              data-testid="cancel-policy-button"
            />
            <FilledButton
              :loading="loading"
              text="Salvar"
              width="100px"
              @click="handleSubmit"
              class="!bg-primary30"
              data-testid="submit-policy-button"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'

import Input from '@/components/designSystem/Inputs/Input.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'

import { storeToRefs } from 'pinia'
import { useCompanyStore } from '@/pinia-store/useCompanyStore'

import { useEscapeKey } from '@/utils/DOMEvents'

export interface CreatePolicyFormData {
  name: string
  companyId: string
}

withDefaults(
  defineProps<{
    modalActive: boolean
    title?: string
    loading?: boolean
  }>(),
  {
    title: 'Criar nova política',
    loading: false
  }
)

const emits = defineEmits<{
  close: []
  submit: [formData: CreatePolicyFormData]
}>()

const { listCompanies } = storeToRefs(useCompanyStore())

useEscapeKey(() => {
  close()
})

const keyRender = ref(Math.random())

const error = reactive({
  name: false,
  companyId: false
})

const form = reactive({
  name: '',
  companyId: ''
})

const isValid = ref(false)

watch(form, () => {
  isValid.value = Object.values(error).every((e) => e !== true)
})

function validateName() {
  error.name = form.name.trim() === ''
}

function validateCompanyId() {
  error.companyId = !form.companyId
}

function validateAll() {
  validateName()
  validateCompanyId()
  isValid.value = Object.values(error).every((e) => e !== true)
}

function capitalizeFirstLetter(text: string): string {
  if (!text) return ''
  return text.toLowerCase().replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase())
}

const companiesFormatted = computed(() => {
  return listCompanies.value.map((company) => ({
    title: capitalizeFirstLetter(company.title),
    value: String(company.value)
  }))
})

function handleSubmit() {
  validateAll()
  if (!isValid.value) return

  emits('submit', {
    name: form.name.trim(),
    companyId: form.companyId
  })
}

function close() {
  Object.keys(form).forEach((key) => {
    ;(form as Record<string, string>)[key] = ''
  })

  Object.keys(error).forEach((key) => {
    ;(error as Record<string, boolean>)[key] = false
  })

  keyRender.value = Math.random()
  emits('close')
}

defineExpose({ close })
</script>

<style scoped>
.modal {
  transition: opacity 0.25s ease;
}

.container-modal {
  width: 700px;
}

.titleBold {
  font-family: 'Inter';
  font-size: 18px;
  font-weight: 700;
}
</style>
